/**
 * One card of Home's feed (D354): a HeroUI Pro `HoloCard` in landscape, so the tilt, the glare
 * and the artwork's drift against the pointer are the component's, not ours. The card's own
 * artwork is its area's icon in its tone; the words get the whole width. Under the face, the
 * actions: useful, not useful, pin, hide, refresh, and the one that opens the item's own view.
 *
 * The action row is a sibling of the HoloCard, over it, not inside it: the HoloCard tracks the
 * pointer from its own surface for the tilt, and a button inside that tree lost its press to the
 * tracking on the centre card. Out here the row takes every click and key press, and each press
 * answers (`feedFeedbackModel.ts`).
 *
 * Ported from the workbench's components/home/FeedCard.tsx, less an app's own view on its card (the console has
 * no apps).
 */
import { ArrowRight, ArrowRotateRight, EyeSlash, Pin, PinSlash, ThumbsDown, ThumbsUp } from '@gravity-ui/icons';
import { Button } from '@heroui/react';
import { HoloCard } from '@heroui-pro/react';

import { type FeedItem, metaLine } from '../../sharedHome.js';
import { Hint } from '../ui/Hint.js';
import { cardGradient, itemIcon } from './feedArt.js';
import { FeedAsk } from './FeedAsk.tsx';
import { recordActs } from './feedAskModel.js';
import type { CardButtonStates } from './feedFeedbackModel.js';

export interface FeedCardProps {
  item: FeedItem;
  /** The card in the middle: the only one that tilts and answers the pointer. */
  centre: boolean;
  /** A press is down on the carousel (a drag may follow): the tilt stands still. */
  pressing: boolean;
  states: CardButtonStates;
  /** What the last press said, for a moment: "Refreshed". */
  said?: string;
  onUseful: () => void;
  onNotUseful: () => void;
  onPin: () => void;
  onHide: () => void;
  onRefresh: () => void;
  onOpen: () => void;
  /** One of the card's other ways in (a summary card has one per kind of thing). */
  onOpenView: (view: string) => void;
  /** The card's question was answered in place: Home is read again. */
  onAnswered: () => void;
}

export function FeedCard({ item, centre, pressing, states, said, onUseful, onNotUseful, onPin, onHide, onRefresh, onOpen, onOpenView, onAnswered }: FeedCardProps) {
  // A card that asks something shows its answers over its face; one that cannot be answered here says why Open is the way.
  const asksHere = recordActs(item).length > 0 || !!item.ask?.openBecause;
  return (
    <div className="home-card" data-tone={item.tone} data-feed-card={item.id} data-kind={item.kind} data-centre={centre || undefined} data-asks={asksHere || undefined}>
      <HoloCard gradient={cardGradient(item.tone)} isInteractive={centre && !pressing} className="home-holo" data-tone={item.tone}>
        <HoloCard.Rotator>
          <HoloCard.Frame>
            <HoloCard.Surface>
              <HoloCard.Inset />
              {/* The area's own icon, large and faint: a picture of what the card is about. */}
              <HoloCard.Content className="home-holo__art" aria-hidden="true">
                <span className="home-holo__icon">{itemIcon(item)}</span>
              </HoloCard.Content>
              <HoloCard.Content className="home-holo__text">
                <span className="home-holo__kicker">{item.kicker}</span>
                <span className="home-holo__meta" data-feed-meta>
                  {metaLine(item)}
                </span>
                <h2 className="home-holo__title">{item.title}</h2>
                {item.summary ? <p className="home-holo__summary">{item.summary}</p> : null}
                <span className="home-holo__because">
                  {item.because}
                  {said ? (
                    <span className="home-holo__say" role="status" data-feed-say>
                      {said}
                    </span>
                  ) : null}
                </span>
              </HoloCard.Content>
              <HoloCard.Shine />
            </HoloCard.Surface>
          </HoloCard.Frame>
        </HoloCard.Rotator>
      </HoloCard>
      {asksHere ? <FeedAsk item={item} onAnswered={onAnswered} /> : null}
      <div className="home-holo__actions" data-feed-actions>
        <div className="home-holo__rate" role="group" aria-label="Was this useful?">
          <Hint text={states.useful ? 'Marked useful: press again to clear' : 'Useful: show more like this'}>
            <Button size="sm" variant="ghost" isIconOnly aria-label="Useful" aria-pressed={states.useful} onPress={onUseful} data-feed-action="useful" data-on={states.useful || undefined}>
              <ThumbsUp className="size-4" />
            </Button>
          </Hint>
          <Hint text={states.notUseful ? 'Marked not useful: press again to clear' : 'Not useful: show fewer like this'}>
            <Button size="sm" variant="ghost" isIconOnly aria-label="Not useful" aria-pressed={states.notUseful} onPress={onNotUseful} data-feed-action="not-useful" data-on={states.notUseful || undefined}>
              <ThumbsDown className="size-4" />
            </Button>
          </Hint>
        </div>
        <Hint text={states.pinned ? 'Unpin' : 'Pin to the front'}>
          <Button size="sm" variant="ghost" isIconOnly aria-label={states.pinned ? 'Unpin' : 'Pin'} aria-pressed={states.pinned} onPress={onPin} data-feed-action="pin" data-on={states.pinned || undefined}>
            {states.pinned ? <PinSlash className="size-4" /> : <Pin className="size-4" />}
          </Button>
        </Hint>
        <Hint text="Hide for a week, or until it changes">
          <Button size="sm" variant="ghost" isIconOnly aria-label="Hide" onPress={onHide} data-feed-action="hide">
            <EyeSlash className="size-4" />
          </Button>
        </Hint>
        <Hint text="Read this card again">
          <Button size="sm" variant="ghost" isIconOnly aria-label="Refresh this card" onPress={onRefresh} data-feed-action="refresh" data-refreshing={states.refreshing || undefined}>
            <ArrowRotateRight className="size-4" />
          </Button>
        </Hint>
        {item.opens && item.opens.length > 1
          ? item.opens.slice(1).map((o) => (
              <Button key={o.label} size="md" variant="secondary" className="home-holo__open-more" onPress={() => onOpenView(o.view)} data-feed-action="open-more">
                {o.label}
              </Button>
            ))
          : null}
        <Button size="md" variant="primary" className="home-holo__open" onPress={onOpen} data-feed-action="open">
          {item.open.label}
          <ArrowRight className="size-4" />
        </Button>
      </div>
    </div>
  );
}
